import { downloadRightToInformationCoverNotePdf } from '../pages/wizard/rightToInformation/rightToInformationCoverNote';
import { downloadCommonCoverNotePdf } from '../services/coverNoteRequests';
import * as criminalService from '../services/criminalCaseRequests';
import type { WizardFlowKind } from './wizardCategoryRoute';
import { usesCommonCoverNote, usesCriminalAppellateCoverNote } from './wizardCategoryRoute';
import { printPdfBlob } from './printPdfBlob';

function normalizePdfBlob(data: Blob): Blob {
  return data.type === 'application/pdf' ? data : new Blob([data], { type: 'application/pdf' });
}

function safeRefName(fileRef?: string): string {
  return (fileRef?.trim() || 'document').replace(/[^\w.-]+/g, '_');
}

async function fetchCoverNotePdf(
  caseRequestId: string,
  flow: WizardFlowKind,
): Promise<{ success: boolean; data?: Blob; message?: string }> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  if (usesCommonCoverNote(flow) || usesCriminalAppellateCoverNote(flow)) {
    const r = await downloadCommonCoverNotePdf(id);
    if (!r.success) return { success: false, message: r.message ?? 'Cover note PDF could not be loaded.' };
    return { success: true, data: normalizePdfBlob(r.data) };
  }

  if (flow === 'right-to-information') {
    const r = await downloadRightToInformationCoverNotePdf(id);
    if (!r.success) return { success: false, message: r.message ?? 'Cover note PDF could not be loaded.' };
    return { success: true, data: normalizePdfBlob(r.data) };
  }

  const r = await criminalService.downloadCriminalCoverNotePdf(id);
  if (!r.success) return { success: false, message: r.message ?? 'Cover note PDF could not be loaded.' };
  return { success: true, data: normalizePdfBlob(r.data) };
}

/** Download cover note PDF from the API for the case type. */
export async function downloadCoverNotePdfFile(
  caseRequestId: string,
  flow: WizardFlowKind,
  fileRef?: string,
): Promise<{ success: boolean; message?: string }> {
  const r = await fetchCoverNotePdf(caseRequestId, flow);
  if (!r.success || !r.data) return { success: false, message: r.message };

  const safeRef = safeRefName(fileRef);
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Cover-Note-${safeRef}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
  return { success: true };
}

/** Fetch cover note PDF from the API and open the browser print dialog. */
export async function printCoverNotePdf(
  caseRequestId: string,
  flow: WizardFlowKind,
  fileRef?: string,
): Promise<{ success: boolean; message?: string }> {
  const r = await fetchCoverNotePdf(caseRequestId, flow);
  if (!r.success || !r.data) return { success: false, message: r.message };

  printPdfBlob(r.data, `Cover-Note-${safeRefName(fileRef)}`);
  return { success: true };
}
